import {faChartPie} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getMenuItemNameForAnalytics} from '@rdcomponents/navigation/navigation-helper';
import {nameForAnalytics} from '@rdcomponents/navigation/navigation-type';
import {useLocation} from 'react-router-dom';

import {appRoute} from '../../../../app-route';
import {RatingHistoryPage} from '../../../../page/main/reviews-analysis/pages/rating-history/rating-history-page';
import {
    ReviewsAnalysisActivationBasePage,
    ReviewsAnalysisActivationFullPage,
    ReviewsAnalysisCitiesPage,
    ReviewsAnalysisConcordancePage,
    ReviewsAnalysisDashboard,
    ReviewsAnalysisKeywordsAndTopicsPage,
    ReviewsAnalysisLocationsPage,
    ReviewsAnalysisPhrasesPage,
    ReviewsAnalysisTopicCreatePage,
    ReviewsAnalysisTopicPage,
    ReviewsAnalysisTopicUpdatePage,
} from '../../../../page/main/reviews-analysis/reviews-analysis-index';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {isReviewPhrasesFeatureEnabled} from '../../../../util/feature-toggle/feature-toggle';
import {NavigationItemConfigType} from '../routing-type';

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
export function useReviewsAnalysisNavigation(): Array<NavigationItemConfigType> {
    const {licenses, availableFeatures, enabledFeatures} = useLicenses();

    const {isWhiteLabel, isDomainFeatureEnabled} = useDomainConfig();

    const {search} = useLocation();
    const {getLocalizedString} = useLocale();

    const onlyShowCardsRatingForWhiteLabel =
        isWhiteLabel &&
        !availableFeatures[FeaturesEnum.reviewAnalyticsBase] &&
        isDomainFeatureEnabled[FeaturesEnum.reviewAnalyticsBase];

    return [
        {
            isAvailable:
                (availableFeatures[FeaturesEnum.reviewAnalyticsBase] || onlyShowCardsRatingForWhiteLabel) &&
                enabledFeatures.includes(FeaturesEnum.reviewAnalyticsBase),
            isActivated: true,
            path: appRoute.reviewsAnalysis.path,
            icon: <FontAwesomeIcon icon={faChartPie} />,
            label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS'),
            [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__REVIEWS_ANALYSIS'),
            search,
            ...(onlyShowCardsRatingForWhiteLabel
                ? {
                      component: RatingHistoryPage,
                  }
                : {
                      redirectPath: appRoute.reviewsAnalysisDashboard.path,
                      deniedPath: [appRoute.reviewsAnalysis.path],
                      children: [
                          {
                              isAvailable: availableFeatures[FeaturesEnum.reviewAnalyticsBase],
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisDashboard.path,
                              label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__DASHBOARD'),
                              [nameForAnalytics]: getMenuItemNameForAnalytics(
                                  'CATEGORY_NAME__REVIEWS_ANALYSIS__DASHBOARD'
                              ),
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsBase]?.isActive
                                  ? ReviewsAnalysisDashboard
                                  : ReviewsAnalysisActivationBasePage,
                          },
                          {
                              isAvailable: availableFeatures[FeaturesEnum.reviewAnalyticsFull],
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisConcordance.path,
                              label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__CONCORDANCE'),
                              [nameForAnalytics]: getMenuItemNameForAnalytics(
                                  'CATEGORY_NAME__REVIEWS_ANALYSIS__CONCORDANCE'
                              ),
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisConcordancePage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isAvailable: availableFeatures[FeaturesEnum.reviewAnalyticsFull],
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisKeywordsAndTopics.path,
                              label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS'),
                              [nameForAnalytics]: getMenuItemNameForAnalytics(
                                  'CATEGORY_NAME__REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS'
                              ),
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisKeywordsAndTopicsPage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isAvailable: isReviewPhrasesFeatureEnabled(),
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisPhrases.path,
                              label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__PHRASES'),
                              [nameForAnalytics]: getMenuItemNameForAnalytics(
                                  'CATEGORY_NAME__REVIEWS_ANALYSIS__PHRASES'
                              ),
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisPhrasesPage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isAvailable: availableFeatures[FeaturesEnum.reviewAnalyticsFull],
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisCompanies.path,
                              label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__COMPANIES'),
                              [nameForAnalytics]: getMenuItemNameForAnalytics(
                                  'CATEGORY_NAME__REVIEWS_ANALYSIS__COMPANIES'
                              ),
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisLocationsPage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isAvailable: Boolean(availableFeatures[FeaturesEnum.reviewAnalyticsFull]),
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisCities.path,
                              label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__CITIES'),
                              [nameForAnalytics]: getMenuItemNameForAnalytics(
                                  'CATEGORY_NAME__REVIEWS_ANALYSIS__CITIES'
                              ),
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisCitiesPage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isOnlyRoute: true,
                              isAvailable: availableFeatures[FeaturesEnum.reviewAnalyticsFull],
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisTopic.path,
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisTopicPage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isOnlyRoute: true,
                              isAvailable: availableFeatures[FeaturesEnum.reviewAnalyticsFull],
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisTopicCreate.path,
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisTopicCreatePage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isOnlyRoute: true,
                              isAvailable: availableFeatures[FeaturesEnum.reviewAnalyticsFull],
                              isActivated: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive,
                              path: appRoute.reviewsAnalysisTopicUpdate.path,
                              exact: true,
                              component: licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive
                                  ? ReviewsAnalysisTopicUpdatePage
                                  : ReviewsAnalysisActivationFullPage,
                          },
                          {
                              isAvailable: true,
                              isActivated: true,
                              path: appRoute.reviewAnalysisGeneratorRatings.path,
                              label: getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__RATINGS'),
                              [nameForAnalytics]: getMenuItemNameForAnalytics(
                                  'CATEGORY_NAME__REVIEWS_ANALYSIS__RATINGS'
                              ),
                              exact: true,
                              component: RatingHistoryPage,
                          },
                      ],
                  }),
        },
    ];
}
